/**
 * Focos dentro de UM município (cartão do município no clique). Funções puras,
 * sem OpenLayers (testadas em countInMunicipio.test.ts).
 *
 * Custo: `indexMunicipioFires` percorre o conjunto UMA vez (no clique, ou
 * quando o conjunto de focos muda com o cartão aberto). Depois, a contagem de
 * qualquer janela de tempo é O(log m) (busca binária + soma prefixada sobre os
 * focos do município), então o cartão acompanha o loop sem recalcular nada.
 *
 * Associação ao município:
 * - focos do diário (e focos de 10 min com par no diário) trazem o código IBGE
 *   (`municipio_id`) — comparação direta;
 * - focos de 10 min sem par no diário não trazem município: caixa envolvente
 *   primeiro e, só dentro dela, ponto-no-polígono contra a geometria do município.
 */
import { CLASS_GOES, type FireDataset, type WindowCount } from './dataset';
import { indexRange } from './timeFilter';

/** Geometria do município na projeção do mapa: caixa [minX, minY, maxX, maxY] e teste de ponto. */
export interface MunicipioShape {
  extent: readonly [number, number, number, number] | readonly number[];
  contains: (x: number, y: number) => boolean;
}

export interface MunicipioFires {
  /** Conjunto de origem (para saber quando refazer). */
  ds: FireDataset;
  /** Instantes dos focos do município (crescente, como no conjunto). */
  t: Float64Array;
  /** goesPrefix[k] = nº de focos GOES-19 em [0, k). */
  goesPrefix: Uint32Array;
}

/** Código IBGE do foco `i` (do próprio diário ou do par no diário); 0 = desconhecido. */
export function fireCdMun(ds: FireDataset, i: number): number {
  const tb = ds.tables[ds.tbl[i]!]!;
  if (tb.extra) return tb.extra.cdMun[ds.row[i]!] ?? 0;
  const et = ds.enrichTbl[i]!;
  if (et >= 0) return ds.tables[et]?.extra?.cdMun[ds.enrichRow[i]!] ?? 0;
  return 0;
}

/**
 * Índices (em ordem de tempo) dos focos do conjunto que estão no município.
 * `cdMun` = código IBGE (texto ou número; null = desconhecido). Sem código, só
 * a geometria decide; sem geometria, focos sem código não entram.
 */
export function indexMunicipioFires(
  ds: FireDataset,
  cdMun: string | number | null,
  shape: MunicipioShape | null,
): MunicipioFires {
  const code = cdMun === null || cdMun === '' ? 0 : Number(cdMun);
  const target = Number.isFinite(code) ? code : 0;
  const [minX, minY, maxX, maxY] = shape ? (shape.extent as number[]) : [0, 0, -1, -1];
  const picked: number[] = [];
  for (let i = 0; i < ds.n; i++) {
    const cd = fireCdMun(ds, i);
    if (cd !== 0 && target !== 0) {
      if (cd === target) picked.push(i);
      continue;
    }
    if (!shape) continue;
    const x = ds.x[i]!;
    const y = ds.y[i]!;
    if (x < minX! || x > maxX! || y < minY! || y > maxY!) continue;
    if (shape.contains(x, y)) picked.push(i);
  }
  const m = picked.length;
  const t = new Float64Array(m);
  const goesPrefix = new Uint32Array(m + 1);
  for (let k = 0; k < m; k++) {
    const i = picked[k]!;
    t[k] = ds.t[i]!;
    goesPrefix[k + 1] = goesPrefix[k]! + (ds.cls[i] === CLASS_GOES ? 1 : 0);
  }
  return { ds, t, goesPrefix };
}

/** Focos do município com from < t ≤ to, por classe (O(log m)). */
export function countMunicipioWindow(mf: MunicipioFires, from: number, to: number): WindowCount {
  const [lo, hi] = indexRange(mf.t, from, to);
  const goes = mf.goesPrefix[hi]! - mf.goesPrefix[lo]!;
  return { total: hi - lo, goes, others: hi - lo - goes };
}
